import HttpError from './HttpError';
import queryString from 'query-string';

export interface Options extends RequestInit {
    user?: {
        authenticated?: boolean;
        token?: string;
    };
}

export const createHeadersFromOptions = (options: Options): HeadersInit => {
    const requestHeaders = (options.headers ||
        new Headers({
            Accept: 'application/json',
        })) as Headers;
    const hasBody = options && options.body;
    const isContentTypeSet = requestHeaders.has('Content-Type');
    const isGetMethod = !options?.method || options?.method === 'GET';
    const isFormData = options?.body instanceof FormData;

    const shouldSetContentType =
        hasBody && !isContentTypeSet && !isGetMethod && !isFormData;
    if (shouldSetContentType) {
        requestHeaders.set('Content-Type', 'application/json');
    }

    if (options.user && options.user.authenticated && options.user.token) {
        requestHeaders.set('Authorization', options.user.token);
    }

    return requestHeaders;
};

interface FetchJsonResponse<T> {
    status: number;
    headers: Headers;
    json: T;
}


export const fetchJson = async <T>(url: string, options: Options = {}): Promise<FetchJsonResponse<T>> => {
    const requestHeaders = createHeadersFromOptions(options);

    try {
        const response = await fetch(url, {...options, headers: requestHeaders});

        // Extract JSON or fallback to plain text
        const contentType = response.headers.get("Content-Type") || "";
        const isJson = contentType.includes("application/json");
        const body = isJson ? await response.json() : await response.text();

        // Check for HTTP errors
        if (!response.ok) {
            throw new HttpError(
                (body && (body.message || response.statusText)) || "Unknown error",
                response.status,
                body,
            );
        }

        return {
            status: response.status,
            headers: response.headers,
            json: body as T,
        };
    } catch (error) {
        // Network or unexpected errors
        console.error(`Fetch failed for ${url}:`, error);
        throw error;
    }
};

export const queryParameters = queryString.stringify;


function isValidObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export const flattenObject = (
    value: Record<string, unknown>, // Input is an object with string keys and unknown values
    path: string[] = []
): Record<string, unknown> => { // Return type is a flattened object with string keys
    if (isValidObject(value)) {
        return Object.assign(
            {},
            ...Object.keys(value).map((key) =>
                flattenObject(value[key] as Record<string, unknown>, path.concat([key]))
            )
        );
    } else {
        return path.length ? {[path.join('.')]: value} : value as Record<string, unknown>;
    }
};